import * as React from 'react';

import { useRouter } from 'next/router';
import { enqueueSnackbar } from 'notistack';

import { useAuth0 } from '@auth0/auth0-react';

import { Prisma, VERIFICATION_STATUS } from '@itp/prisma-talent/generated/client';
import { queryGetUser } from '@itp/prisma-talent/gql';
import { mergePermissions, safeJSONparse } from '@itp/prisma-talent/helpers/mergePermissions';
import {
  calculateProfileScore,
  IRolePermissions,
  QueryGetUser,
  usePrivateApollo,
  User,
  UserCompany,
  UserContextProps,
  UserContextValues,
} from '@itp/talent';

import { ADMIN_PERMISSION, AUTH_AUDIENCE, IMPERSONATE_PERMISSION } from '../constants/config';
import { removeAuthCookie, setAuthCookie, useLocalAuth } from './localAuthContext';

export const UserContext = React.createContext<UserContextValues>({ setUser: () => {} });
export const COMPANY_QUERYSTRING_ID = 'company-querystring';

export const UserProvider: React.FC<UserContextProps> = ({ children }) => {
  const { isAuthenticated: isAuth0Authenticated, user: userAuth } = useAuth0();
  const { isAuthenticated: isLocalAuthenticated } = useLocalAuth();
  const isAuthenticated = isAuth0Authenticated || isLocalAuthenticated;
  const router = useRouter();
  const companyIdFromQuery = router.pathname.startsWith('/company')
    ? (router.query.company_id as string)
    : null;

  const localStorageUser =
    typeof window !== 'undefined'
      ? (safeJSONparse(localStorage.getItem('user')) as User)
      : undefined;
  const localUser = localStorageUser ? { ...localStorageUser, loading: true } : undefined;

  const [user, setUser] = React.useState<User>(
    localUser || {
      companies: [],
      email: null,
      featureFlags: [],
      id: null,
      isAdmin: false,
      isImpersonate: false,
      loading: isAuthenticated,
      permissions: null,
      profile: null,
    },
  );
  const [snackbarShown, setSnackbarShown] = React.useState<boolean>(false);

  const { data, loading, refetch } = usePrivateApollo<QueryGetUser>({
    context: {
      headers: {
        'tp-domain': 'talent',
      },
    },
    query: queryGetUser,
    skip: !isAuthenticated,
    variables: {
      where: {
        company: {
          is: {
            deletedAt: null,
          },
        },
      } as Prisma.companyOnUserWhereInput,
    },
  });

  React.useEffect(() => {
    localStorage.setItem('user', JSON.stringify(user));
  }, [localStorageUser, user]);

  React.useEffect(() => {
    if (isAuthenticated) {
      setAuthCookie();
    } else if (!isAuth0Authenticated && !isLocalAuthenticated) {
      removeAuthCookie();
    }
  }, [isAuthenticated, isAuth0Authenticated, isLocalAuthenticated]);

  React.useEffect(() => {
    if (!isAuth0Authenticated) return;

    setUser((state) => ({
      ...state,
      isAdmin:
        userAuth?.['https://extra/claims/roles']?.[AUTH_AUDIENCE]?.includes(ADMIN_PERMISSION) ??
        false,
      isImpersonate:
        userAuth?.['https://extra/claims/roles']?.[AUTH_AUDIENCE]?.includes(
          IMPERSONATE_PERMISSION,
        ) ?? false,
    }));
  }, [isAuth0Authenticated, userAuth]);

  React.useEffect(() => {
    const getSelectedCompany = () => {
      const companies = data?.companies?.filter((company) => {
        const status = company?.company?.verificationStatus;
        return status !== VERIFICATION_STATUS.rejected && status !== VERIFICATION_STATUS.deleted;
      });

      const lastCompanyId =
        companyIdFromQuery || safeJSONparse(localStorage.getItem('selectedCompanyId'));

      const selectedCompany = companies?.find((company) =>
        lastCompanyId
          ? company?.company?.id === lastCompanyId
          : company?.company?.id === user?.selectedCompanyId,
      );

      if (companyIdFromQuery && selectedCompany && !snackbarShown) {
        localStorage.setItem('selectedCompanyId', companyIdFromQuery);
        router.replace(router.pathname);

        enqueueSnackbar('Company has been changed', {
          autoHideDuration: 3000,
          variant: 'success',
        });
        setSnackbarShown(true);
      }
      return selectedCompany || companies?.[0];
    };

    if (data !== undefined && !loading) {
      const selectedCompany: UserCompany = getSelectedCompany();
      const profileScore = calculateProfileScore(data?.profile);
      const userPermissions = (selectedCompany?.role?.permissions ||
        selectedCompany?.role?.defaultPermissions) as IRolePermissions;
      const companyPermissions = selectedCompany?.company.permissions
        ?.permissions as IRolePermissions;
      const permissions = mergePermissions(userPermissions, companyPermissions) as IRolePermissions;

      setUser((state) => ({
        ...state,
        companies: data?.companies,
        email: data?.email,
        featureFlags: data?.featureFlags,
        id: data?.id,
        loading,
        permissions,
        profile: data?.profile,
        profileScore,
        redirect: data?.redirect,
        selectedCompany,
        selectedCompanyId: selectedCompany?.company?.id,
      }));
    }
  }, [companyIdFromQuery, data, loading, router, snackbarShown, user?.selectedCompanyId]);

  const value: UserContextValues = { refetch, setUser, user };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
